@if (isLoading()) {
  <div class="flex justify-center items-center h-64">
    <mat-spinner diameter="48" />
  </div>
} @else {
  <div class="p-5">
    <app-breadcrumb [items]="breadcrumbItems()" />

    <!-- Header -->
    <div class="flex justify-between items-center mb-5">
      <h1 class="text-2xl font-medium">
        @if (isEditMode() && spotTitle()) {
          {{ spotTitle() }}
        } @else {
          {{ 'waters.fishing-spots.form.create-title' | translate }}
        }
      </h1>
      <div class="flex gap-2">
        @if (isEditMode()) {
          <button
            mat-stroked-button
            color="warn"
            class="!min-w-0 !px-2"
            [disabled]="isDeleting() || isSaving()"
            [matTooltip]="'waters.form.delete' | translate"
            (click)="delete()">
            @if (isDeleting()) {
              <mat-spinner diameter="18" />
            } @else {
              <span class="flex items-center leading-none"><mat-icon>delete</mat-icon></span>
            }
          </button>
        }
        <button mat-stroked-button [disabled]="isSaving() || isDeleting()" (click)="cancel()">
          {{ 'waters.form.cancel' | translate }}
        </button>
        <button mat-flat-button color="primary" [disabled]="isSaving() || isDeleting()" (click)="save()">
          @if (isSaving()) {
            <mat-spinner diameter="18" />
          } @else {
            {{ 'waters.form.save' | translate }}
          }
        </button>
      </div>
    </div>

    <form [formGroup]="form" class="flex flex-col gap-4">

      <!-- General card -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.fishing-spots.form.section.general' | translate }}</mat-card-title>
          <mat-card-subtitle>{{ 'waters.fishing-spots.form.section.general-sub' | translate }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="!pt-4 flex flex-col gap-4">
          <!-- Row 1: Number -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <mat-form-field>
              <mat-label>{{ 'waters.fishing-spots.form.number' | translate }}</mat-label>
              <input matInput type="number" formControlName="number" />
              <mat-error>{{ 'generic.required' | translate }}</mat-error>
            </mat-form-field>
          </div>

          <!-- Row 2: Fishermen count | Min fishermen | Co-travelers -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
            <mat-form-field>
              <mat-label>{{ 'waters.fishing-spots.form.fishermenCount' | translate }}</mat-label>
              <input matInput type="number" formControlName="spots" min="0" />
              <mat-error>{{ 'generic.required' | translate }}</mat-error>
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'waters.fishing-spots.form.minimumFishermen' | translate }}</mat-label>
              <input matInput type="number" formControlName="minimumFishermen" min="0" />
            </mat-form-field>
            <mat-form-field>
              <mat-label>{{ 'waters.fishing-spots.form.coTravelers' | translate }}</mat-label>
              <input matInput type="number" formControlName="coTravelers" min="0" />
              <mat-error>{{ 'generic.required' | translate }}</mat-error>
            </mat-form-field>
          </div>

          <!-- Row 3: DogsAllowed select -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <mat-form-field>
              <mat-label>{{ 'waters.fishing-spots.form.dogsAllowed' | translate }}</mat-label>
              <mat-select formControlName="dogsAllowed">
                <mat-option value="inherit">{{ 'waters.fishing-spots.form.dogsAllowed.inherit' | translate }}</mat-option>
                <mat-option [value]="true">{{ 'waters.fishing-spots.form.dogsAllowed.yes' | translate }}</mat-option>
                <mat-option [value]="false">{{ 'waters.fishing-spots.form.dogsAllowed.no' | translate }}</mat-option>
              </mat-select>
            </mat-form-field>
          </div>

          <!-- Row 4: Toggles -->
          <div class="flex gap-6 flex-wrap">
            <mat-slide-toggle formControlName="isActive">
              {{ 'waters.fishing-spots.form.isActive' | translate }}
            </mat-slide-toggle>
            <mat-slide-toggle formControlName="isDummy">
              {{ 'waters.fishing-spots.form.isDummy' | translate }}
            </mat-slide-toggle>
          </div>
        </mat-card-content>
      </mat-card>

      <!-- Description card -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.fishing-spots.form.section.description' | translate }}</mat-card-title>
          <mat-card-subtitle>{{ 'waters.fishing-spots.form.section.description-sub' | translate }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="!pt-4">
          <app-localized-text-editor
            [nl]="form.controls.descriptionNl"
            [en]="form.controls.descriptionEn"
            [de]="form.controls.descriptionDe"
            [it]="form.controls.descriptionIt"
            [fr]="form.controls.descriptionFr"
          />
        </mat-card-content>
      </mat-card>

      <!-- Pricing per combination card. In create mode the unit id is 0 until the form's own save
           returns; the grid is filled in advance and PUT with the new id in the same submit. -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.fishing-spots.form.section.pricing' | translate }}</mat-card-title>
          <mat-card-subtitle>{{ 'waters.fishing-spots.form.section.pricing-sub' | translate }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="!pt-4">
          <app-combination-pricing
            [waterId]="combinationWaterId()"
            [unitId]="combinationSpotId()"
            unitType="fishing-spot"
            [spotsControl]="form.controls.spots"
            [minimumFishermenControl]="form.controls.minimumFishermen"
            [coTravelersControl]="form.controls.coTravelers"
          />
        </mat-card-content>
      </mat-card>

      <!-- Location card -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.fishing-spots.form.section.location' | translate }}</mat-card-title>
          <mat-card-subtitle>{{ 'waters.fishing-spots.form.section.location-sub' | translate }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="!pt-4 flex flex-col gap-4">
          <app-location-picker
            [latControl]="form.controls.latitude"
            [lngControl]="form.controls.longitude"
            [hintLat]="waterHintLat()"
            [hintLng]="waterHintLng()"
          />
        </mat-card-content>
      </mat-card>

      <!-- Image card -->
      <mat-card>
        <mat-card-header>
          <mat-card-title>{{ 'waters.fishing-spots.form.section.image' | translate }}</mat-card-title>
          <mat-card-subtitle>{{ 'waters.fishing-spots.form.section.image-sub' | translate }}</mat-card-subtitle>
        </mat-card-header>
        <mat-card-content class="!pt-4 flex flex-col gap-4">
          <app-image-picker
            [currentImageName]="imageName()"
            [currentImageUrl]="imageUrl()"
            (fileSelected)="onImageFileSelected($event)"
          />
        </mat-card-content>
      </mat-card>

    </form>
  </div>
}
